import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, ChartBar, Spinner } from "@phosphor-icons/react";
import { getOutletId, clearOutletId } from "../utils/appNavigation";

export default function AppAnalytics() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const params = new URLSearchParams(location.search);
  const outletId = getOutletId(params);
  
  const [outlet, setOutlet] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!outletId) {
      navigate("/app/settings");
      return;
    }

    const fetchData = async () => {
      try {
        const [outletRes, statsRes] = await Promise.all([
          api.get(`/outlets/${outletId}`),
          api.get(`/outlets/${outletId}/analytics`),
        ]);
        
        if (!outletRes.data) {
          clearOutletId();
          navigate("/app/settings");
          return;
        }
        
        setOutlet(outletRes.data);
        setStats(statsRes.data);
      } catch (e) {
        setError("Failed to load analytics");
        toast.error("Could not load data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [outletId, navigate]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
        <div className="tag">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-5" style={{ background: "var(--bg)" }}>
        <div className="card-pop p-8 text-center max-w-sm">
          <h2 className="display text-xl font-black mb-3">Error</h2>
          <p className="text-[var(--ink-muted)] mb-4">{error}</p>
          <button onClick={() => navigate("/app/settings")} className="btn-primary">
            Go to Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      {/* Header */}
      <header className="sticky top-0 z-10 px-5 py-4 flex items-center gap-3" style={{ background: "var(--bg)", borderBottom: "1.5px solid var(--ink)" }}>
        <button
          onClick={handleBack}
          className="w-9 h-9 flex items-center justify-center rounded-lg transition-all hover:opacity-70"
          style={{ border: "1.5px solid var(--ink)" }}
          aria-label="Back"
        >
          <ArrowLeft size={20} weight="bold" />
        </button>
        <h1 className="display text-xl font-black">Analytics</h1>
      </header>

      {/* Main Content */}
      <main className="p-5 max-w-xl mx-auto w-full pb-8">
        {outlet && (
          <div className="mb-4">
            <h2 className="text-lg font-bold">{outlet.name}</h2>
          </div>
        )}

        {stats ? (
          <div className="space-y-4">
            {/* Total Spins */}
            <div className="card-pop p-5 flex items-center justify-between" style={{ background: "var(--pastel-blue)" }}>
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ background: "var(--secondary)" }}>
                  <Spinner size={24} weight="bold" />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Total Spins</div>
                  <div className="text-2xl font-black display">{stats.spin_attempts || 0}</div>
                </div>
              </div>
            </div>

            {/* Redeemed */}
            <div className="card-pop p-5 flex items-center justify-between" style={{ background: "var(--pastel-green)" }}>
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ background: "var(--success)", color: "#fff" }}>
                  <ChartBar size={24} weight="bold" />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Redeemed</div>
                  <div className="text-2xl font-black display">{stats.redemptions || 0}</div>
                </div>
              </div>
            </div>

            {/* Conversion Rate */}
            <div className="card-pop p-5 flex items-center justify-between" style={{ background: "var(--pastel-yellow)" }}>
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ background: "var(--primary)", color: "#fff" }}>
                  <span className="text-lg font-bold">%</span>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Conversion</div>
                  <div className="text-2xl font-black display">{stats.redemption_rate || 0}%</div>
                </div>
              </div>
            </div>

            {/* Simple explanation */}
            <div className="mt-6 text-sm text-[var(--ink-muted)] text-center">
              <p>Of {stats.spin_attempts || 0} spins, {stats.redemptions || 0} were redeemed.</p>
            </div>
          </div>
        ) : (
          <div className="card-pop p-8 text-center">
            <p className="text-[var(--ink-muted)]">No data available</p>
          </div>
        )}
      </main>
    </div>
  );
}